'use client';
import React, { useState } from 'react';
import Image from 'next/image';
import RatingStars from '@/components/ui/RatingStars';
import StockBadge from '@/components/common/StockBadge';
import { ImageDTO } from '@/types/product';

interface ProductGalleryProps {
  productId: number;
  mainImages: ImageDTO[];
  thumbnails: ImageDTO[];
  rating: number;
  reviewCount: number;
  onFavoriteToggle?: () => void;
  isFavorite?: boolean;
  className?: string;
}

const ProductGallery: React.FC<ProductGalleryProps> = ({
  productId,
  mainImages,
  thumbnails,
  rating,
  reviewCount,
  onFavoriteToggle,
  isFavorite = false,
  className = '',
}) => {
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [currentMainImage, setCurrentMainImage] = useState(mainImages[0]);
  const [thumbnailPage, setThumbnailPage] = useState(0);

  // Calcular cuántas páginas de thumbnails necesitamos (2 por página)
  const thumbnailsPerPage = 2;
  const totalThumbnailPages = Math.ceil(thumbnails.length / thumbnailsPerPage);

  const handleThumbnailClick = (index: number) => {
    setCurrentImageIndex(index);
    setCurrentMainImage(mainImages[index]);
  };

  const handlePreviousImage = () => {
    const newIndex = currentImageIndex > 0 ? currentImageIndex - 1 : thumbnails.length - 1;
    handleThumbnailClick(newIndex);
  };

  const handleNextImage = () => {
    const newIndex = currentImageIndex < thumbnails.length - 1 ? currentImageIndex + 1 : 0;
    handleThumbnailClick(newIndex);
  };

  const handlePreviousThumbnailPage = () => {
    setThumbnailPage((prev) => (prev > 0 ? prev - 1 : totalThumbnailPages - 1));
  };

  const handleNextThumbnailPage = () => {
    setThumbnailPage((prev) => (prev < totalThumbnailPages - 1 ? prev + 1 : 0));
  };

  // Obtener los thumbnails de la página actual
  const getCurrentThumbnails = () => {
    const startIndex = thumbnailPage * thumbnailsPerPage;
    return thumbnails.slice(startIndex, startIndex + thumbnailsPerPage);
  };

  return (
    <div data-component-id="src/components/sections/ProductGallery.tsx:67:4" data-component-path="src/components/sections/ProductGallery.tsx" data-component-line="67" data-component-file="ProductGallery.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%7D" className={`flex flex-col gap-3 flex-1 ${className}`}>
      {/* Main Image Container */}
      <div data-component-id="src/components/sections/ProductGallery.tsx:69:6" data-component-path="src/components/sections/ProductGallery.tsx" data-component-line="69" data-component-file="ProductGallery.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22relative%20bg-slider-6%20rounded-%5B17px%5D%20w-full%20max-w-%5B836px%5D%20h-%5B578px%5D%20p-5%20mx-auto%22%7D" className="relative bg-slider-6 rounded-[17px] w-full max-w-[836px] h-[578px] p-5 mx-auto">
        <StockBadge data-component-id="src/components/sections/ProductGallery.tsx:70:8" data-component-path="src/components/sections/ProductGallery.tsx" data-component-line="70" data-component-file="ProductGallery.tsx" data-component-name="StockBadge" data-component-content="%7B%22elementName%22%3A%22StockBadge%22%7D" productId={productId} />
        {/* Rating Stars */}
        {/* TODO: Añadir el rating y el review count
        <div className="flex flex-row items-center gap-2 mb-5">
          <RatingStars rating={rating} readonly />
          <span className="text-[#3D1C1A] font-tt-norms font-medium text-base leading-5 ml-3">
            ({reviewCount})
          </span>
        </div>
        */}
        {/* Favorite Icon */}
        {/* TODO: Añadir el icono de favorito
        <button 
          onClick={onFavoriteToggle}
          className="absolute top-5 right-5 cursor-pointer transition-transform hover:scale-110"
        >
          <Image 
            src={isFavorite ? "/images/img_favoritos_v2.svg" : "/images/img_vector_gray_900_24x28.svg"}
            alt="Add to favorites"
            width={28}
            height={24}
          />
        </button>
        */}

        {/* Main Product Image */}
        <div data-component-id="src/components/sections/ProductGallery.tsx:96:8" data-component-path="src/components/sections/ProductGallery.tsx" data-component-line="96" data-component-file="ProductGallery.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22flex%20justify-center%20items-center%20h-%5B465px%5D%20overflow-hidden%22%7D" className="flex justify-center items-center h-[465px] overflow-hidden">
          <img data-component-id="src/components/sections/ProductGallery.tsx:97:10" data-component-path="src/components/sections/ProductGallery.tsx" data-component-line="97" data-component-file="ProductGallery.tsx" data-component-name="img" data-component-content="%7B%22elementName%22%3A%22img%22%2C%22className%22%3A%22max-w-full%20max-h-full%20object-contain%22%7D"
            src={currentMainImage?.src || '/images/img_pasta_mista_6_copia_2.png'}
            alt={currentMainImage?.alt || 'Imagen del producto'}
            className="max-w-full max-h-full object-contain"
          />
        </div>
      </div>

      {/* Thumbnail Carousel Container */}
      <div data-component-id="src/components/sections/ProductGallery.tsx:106:6" data-component-path="src/components/sections/ProductGallery.tsx" data-component-line="106" data-component-file="ProductGallery.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22relative%20max-w-%5B836px%5D%20mx-auto%22%7D" className="relative max-w-[836px] mx-auto">
        {/* Thumbnail Images Grid */}
        <div data-component-id="src/components/sections/ProductGallery.tsx:108:8" data-component-path="src/components/sections/ProductGallery.tsx" data-component-line="108" data-component-file="ProductGallery.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22flex%20flex-row%20gap-6%20justify-center%22%7D" className="flex flex-row gap-6 justify-center">
          {getCurrentThumbnails().map((thumbnail, index) => {
            const actualIndex = thumbnailPage * thumbnailsPerPage + index;
            const isSelected = actualIndex === currentImageIndex;

            return (
              <div data-component-id="src/components/sections/ProductGallery.tsx:114:14" data-component-path="src/components/sections/ProductGallery.tsx" data-component-line="114" data-component-file="ProductGallery.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%7D"
                key={actualIndex}
                className={`relative w-[408px] h-[232px] rounded-[17px] flex items-center justify-center ${
                  isSelected ? 'shadow-[0_4px_20px_rgba(61,28,26,0.15)]' : ''
                }`}
              >
                <img data-component-id="src/components/sections/ProductGallery.tsx:120:16" data-component-path="src/components/sections/ProductGallery.tsx" data-component-line="120" data-component-file="ProductGallery.tsx" data-component-name="img" data-component-content="%7B%22elementName%22%3A%22img%22%2C%22className%22%3A%22object-contain%20cursor-pointer%20transition-all%20duration-300%20hover%3Aopacity-80%20max-w-full%20max-h-full%22%7D"
                  src={thumbnail.src}
                  alt={thumbnail.alt || 'Imagen del producto'}
                  width={thumbnail.width || 408}
                  height={thumbnail.height || 232}
                  className="object-contain cursor-pointer transition-all duration-300 hover:opacity-80 max-w-full max-h-full"
                  onClick={() => handleThumbnailClick(actualIndex)}
                />

                {/* Navigation Arrows - Solo mostrar en los bordes */}
                {index === 0 && totalThumbnailPages > 1 && (
                  <button data-component-id="src/components/sections/ProductGallery.tsx:131:18" data-component-path="src/components/sections/ProductGallery.tsx" data-component-line="131" data-component-file="ProductGallery.tsx" data-component-name="button" data-component-content="%7B%22elementName%22%3A%22button%22%2C%22className%22%3A%22absolute%20-left-4%20top-1%2F2%20transform%20-translate-y-1%2F2%20z-10%20bg-%5B%23FEF5E6%5D%20rounded-tl-%5B10px%5D%20rounded-bl-%5B10px%5D%20w-10%20h-10%20flex%20items-center%20justify-center%20hover%3Abg-%5B%233D1C1A%5D%20hover%3Atext-white%20transition-all%20duration-200%20shadow-lg%20border%20border-%5B%233D1C1A%5D%2F20%22%7D"
                    onClick={handlePreviousThumbnailPage}
                    className="absolute -left-4 top-1/2 transform -translate-y-1/2 z-10 bg-[#FEF5E6] rounded-tl-[10px] rounded-bl-[10px] w-10 h-10 flex items-center justify-center hover:bg-[#3D1C1A] hover:text-white transition-all duration-200 shadow-lg border border-[#3D1C1A]/20"
                  >
                    <img data-component-id="src/components/sections/ProductGallery.tsx:135:20" data-component-path="src/components/sections/ProductGallery.tsx" data-component-line="135" data-component-file="ProductGallery.tsx" data-component-name="img" data-component-content="%7B%22elementName%22%3A%22img%22%2C%22src%22%3A%22%2Fimages%2Fimg_vector_gray_900_16x9.svg%22%2C%22alt%22%3A%22Previous%20thumbnails%22%2C%22className%22%3A%22opacity-80%22%7D"
                      src="/images/img_vector_gray_900_16x9.svg"
                      alt="Previous thumbnails"
                      width={14}
                      height={8}
                      className="opacity-80"
                    />
                  </button>
                )}

                {index === getCurrentThumbnails().length - 1 && totalThumbnailPages > 1 && (
                  <button data-component-id="src/components/sections/ProductGallery.tsx:146:18" data-component-path="src/components/sections/ProductGallery.tsx" data-component-line="146" data-component-file="ProductGallery.tsx" data-component-name="button" data-component-content="%7B%22elementName%22%3A%22button%22%2C%22className%22%3A%22absolute%20-right-4%20top-1%2F2%20transform%20-translate-y-1%2F2%20z-10%20bg-%5B%23FEF5E6%5D%20rounded-tr-%5B10px%5D%20rounded-br-%5B10px%5D%20w-10%20h-10%20flex%20items-center%20justify-center%20hover%3Abg-%5B%233D1C1A%5D%20hover%3Atext-white%20transition-all%20duration-200%20shadow-lg%20border%20border-%5B%233D1C1A%5D%2F20%22%7D"
                    onClick={handleNextThumbnailPage}
                    className="absolute -right-4 top-1/2 transform -translate-y-1/2 z-10 bg-[#FEF5E6] rounded-tr-[10px] rounded-br-[10px] w-10 h-10 flex items-center justify-center hover:bg-[#3D1C1A] hover:text-white transition-all duration-200 shadow-lg border border-[#3D1C1A]/20"
                  >
                    <img data-component-id="src/components/sections/ProductGallery.tsx:150:20" data-component-path="src/components/sections/ProductGallery.tsx" data-component-line="150" data-component-file="ProductGallery.tsx" data-component-name="img" data-component-content="%7B%22elementName%22%3A%22img%22%2C%22src%22%3A%22%2Fimages%2Fimg_vector_gray_900_v2.svg%22%2C%22alt%22%3A%22Next%20thumbnails%22%2C%22className%22%3A%22opacity-80%22%7D"
                      src="/images/img_vector_gray_900_v2.svg"
                      alt="Next thumbnails"
                      width={14}
                      height={8}
                      className="opacity-80"
                    />
                  </button>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default ProductGallery;
